import React, { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { formatApiError } from "@/lib/utils";
import { ShieldCheck, Search, History } from "lucide-react";

// CS 21.87 -- HO-only repair of sales reverted by a stock re-import + price-change log viewer.

const fmt = (v) => (v == null ? "—" : Number(v).toLocaleString());
const day = (v) => (v ? String(v).slice(0, 10) : "—");

function RevertedSalesRepair({ wid, canEdit }) {
  const [items, setItems] = useState(null);
  const [picked, setPicked] = useState(new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const scan = async () => {
    try {
      const { data } = await api.get(`/wholesalers/${wid}/repair/reverted-sales`);
      setItems(data.items);
      setPicked(new Set(data.items.map((i) => i.jewel_code)));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Scan failed");
    }
  };

  const toggle = (jc) => setPicked((s) => {
    const n = new Set(s);
    if (n.has(jc)) n.delete(jc);
    else n.add(jc);
    return n;
  });

  const apply = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/wholesalers/${wid}/repair/reverted-sales/apply`, { jewel_codes: Array.from(picked) });
      toast.success(`Restored ${data.restored.length} sale(s)${data.skipped.length ? ` · ${data.skipped.length} skipped` : ""}`);
      setConfirmOpen(false);
      await scan();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Repair failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="luxe-card p-6 space-y-4" data-testid="repair-reverted-sales-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-serif text-xl text-[#0D1B2A] flex items-center gap-2"><ShieldCheck className="w-5 h-5" /> Restore reverted sales</h3>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">
            Finds sold pieces that a stock re-upload put back to available. Values come only from each piece's saved
            sale record; every piece is backed up before the change. Nothing changes until you confirm.
          </p>
        </div>
        <Button onClick={scan} variant="outline" data-testid="repair-scan-btn"><Search className="w-4 h-4 mr-2" /> Scan (preview)</Button>
      </div>

      {items && items.length === 0 && (
        <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md p-3" data-testid="repair-none-found">
          No reverted sales found.
        </div>
      )}
      {items && items.length > 0 && (
        <>
          <div className="overflow-x-auto border border-[#0D1B2A]/10 rounded-md">
            <table className="w-full text-sm" data-testid="repair-preview-table">
              <thead className="bg-[#FAF8F3] text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="p-2"></th><th className="p-2 text-left">Jewel code</th>
                  <th className="p-2 text-left">Now</th><th className="p-2 text-left">Restore to</th>
                  <th className="p-2 text-left">Client</th><th className="p-2 text-right">Sold price</th>
                  <th className="p-2 text-left">Sold date</th><th className="p-2 text-right">Locked MRP</th>
                </tr>
              </thead>
              <tbody>
                {items.map((i) => (
                  <tr key={i.jewel_code} className="border-t border-[#0D1B2A]/5" data-testid={`repair-row-${i.jewel_code}`}>
                    <td className="p-2">
                      <input type="checkbox" checked={picked.has(i.jewel_code)} onChange={() => toggle(i.jewel_code)}
                        data-testid={`repair-pick-${i.jewel_code}`} />
                    </td>
                    <td className="p-2 font-mono">{i.jewel_code}</td>
                    <td className="p-2 text-amber-700">{i.current_status}</td>
                    <td className="p-2 text-emerald-700">sold</td>
                    <td className="p-2">{i.restore_sold_client_name}</td>
                    <td className="p-2 text-right font-mono">{fmt(i.restore_sold_price)}</td>
                    <td className="p-2">{day(i.restore_sold_date)}</td>
                    <td className="p-2 text-right font-mono">{i.customer_override_locked ? fmt(i.customer_override) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {canEdit && (
            <Button onClick={() => setConfirmOpen(true)} disabled={picked.size === 0}
              className="bg-[#0D1B2A] text-white hover:bg-[#0D1B2A]/90" data-testid="repair-open-confirm-btn">
              Restore {picked.size} selected
            </Button>
          )}
        </>
      )}

      <Dialog open={confirmOpen} onOpenChange={(o) => !busy && setConfirmOpen(o)}>
        <DialogContent className="bg-white" data-testid="repair-confirm-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Restore {picked.size} sale(s)?</DialogTitle></DialogHeader>
          <DialogDescription className="text-sm text-slate-600">
            These pieces go back to Sold with the client, price and date from their saved sale record.
            Each is backed up first and a "restored" entry is added to its history.
          </DialogDescription>
          <div className="font-mono text-xs bg-slate-50 border rounded-md p-2 max-h-32 overflow-y-auto" data-testid="repair-confirm-codes">
            {Array.from(picked).join(", ")}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={busy} data-testid="repair-cancel-btn">Cancel</Button>
            <Button onClick={apply} disabled={busy} className="bg-emerald-700 text-white hover:bg-emerald-800" data-testid="repair-apply-btn">
              {busy ? "Restoring…" : "Confirm restore"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PriceLog({ wid }) {
  const [code, setCode] = useState("");
  const [rows, setRows] = useState(null);
  const load = async () => {
    try {
      const { data } = await api.get(`/wholesalers/${wid}/price-log`, { params: code.trim() ? { jewel_code: code.trim() } : {} });
      setRows(data.items);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };
  const val = (v, l) => (v == null ? "engine" : `${fmt(v)}${l ? " (locked)" : ""}`);
  return (
    <div className="luxe-card p-6 space-y-4" data-testid="price-log-card">
      <h3 className="font-serif text-xl text-[#0D1B2A] flex items-center gap-2"><History className="w-5 h-5" /> Price-change log</h3>
      <p className="text-sm text-slate-500">Every manual and bulk change to per-piece prices (B2B / Suggested MRP), newest first.</p>
      <div className="flex gap-2">
        <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Jewel code (blank = all)"
          className="max-w-xs" data-testid="price-log-code-input" onKeyDown={(e) => e.key === "Enter" && load()} />
        <Button variant="outline" onClick={load} data-testid="price-log-load-btn">Show</Button>
      </div>
      {rows && (rows.length === 0 ? (
        <div className="text-sm text-slate-400" data-testid="price-log-empty">No price changes logged yet.</div>
      ) : (
        <div className="overflow-x-auto border border-[#0D1B2A]/10 rounded-md">
          <table className="w-full text-sm" data-testid="price-log-table">
            <thead className="bg-[#FAF8F3] text-xs uppercase tracking-wider text-slate-500">
              <tr><th className="p-2 text-left">When</th><th className="p-2 text-left">Jewel code</th><th className="p-2 text-left">Price</th>
                <th className="p-2 text-right">Before</th><th className="p-2 text-right">After</th><th className="p-2 text-left">Source</th></tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={`${r.at}-${r.jewel_code}-${r.tier}`} className="border-t border-[#0D1B2A]/5" data-testid={`price-log-row-${i}`}>
                  <td className="p-2 text-xs">{String(r.at).slice(0, 19).replace("T", " ")}</td>
                  <td className="p-2 font-mono">{r.jewel_code}</td>
                  <td className="p-2">{r.tier === "customer" ? "Suggested MRP" : "B2B"}</td>
                  <td className="p-2 text-right font-mono">{val(r.old_value, r.old_locked)}</td>
                  <td className="p-2 text-right font-mono">{val(r.new_value, r.new_locked)}</td>
                  <td className="p-2 text-xs text-slate-500">{r.source.replace(/_/g, " ")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

export const WholesalerMaintenance = ({ wid, canEdit }) => (
  <div className="space-y-6" data-testid="wholesaler-maintenance">
    <RevertedSalesRepair wid={wid} canEdit={canEdit} />
    <PriceLog wid={wid} />
  </div>
);
